import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Search, Download, Printer, RefreshCw, ChevronDown, ChevronUp, ShieldCheck } from "lucide-react";
import api, { formatApiErrorDetail } from "../../lib/api";
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, LineChart, Line, Legend,
} from "recharts";

const CONTACT_TYPES = [
  "Initial Call","Return Call","Phone Support","Text Contact","Email / Message Support",
  "Video Visit","In-Person Visit","Home Visit","Hospital Visit","Workplace Visit",
  "Follow-Up Visit","Family Contact","Prayer / Spiritual Support","Funeral / Memorial Attendance",
  "Resource Provided","Referral","No Answer / Voicemail","Other",
];

const csvEscape = (v) => {
  let s = v == null ? "" : String(v);
  if (/^[=+\-@]/.test(s)) s = "'" + s;                     // formula injection guard
  if (/[",\n]/.test(s)) return '"' + s.replace(/"/g, '""') + '"';
  return s;
};

const todayIso = () => new Date().toISOString().slice(0, 10);
const monthKey = (d) => d.slice(0, 7);
const daySpan = (from, to) => {
  if (!from || !to) return 0;
  return Math.round((new Date(to) - new Date(from)) / 86400000) + 1;
};

const FILTER_KEYS = ["date_from", "date_to", "company", "staff_name", "contact_type", "status_filter", "follow_up_needed", "follow_up_date_from", "follow_up_date_to", "area_of_support", "q"];

export default function AdminActivityReports() {
  const [searchParams] = useSearchParams();
  const [filters, setFilters] = useState(() => {
    const init = { date_from: "", date_to: "", company: "", staff_name: "", contact_type: "", status_filter: "", follow_up_needed: "", follow_up_date_from: "", follow_up_date_to: "", area_of_support: "", q: "" };
    FILTER_KEYS.forEach((k) => { const v = searchParams.get(k); if (v != null) init[k] = v; });
    return init;
  });
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({ total_activities: 0, unique_employees: 0, unique_chaplains: 0, with_follow_up: 0 });
  const [byArea, setByArea] = useState([]);
  const [areasOfSupport, setAreasOfSupport] = useState([]);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    api.get("/admin/support-requests/areas-of-support").then((r) => setAreasOfSupport(r.data || [])).catch(() => {});
  }, []);

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      Object.entries(filters).forEach(([k, v]) => {
        if (v === "" || v == null) return;
        params[k] = v;
      });
      const r = await api.get("/admin/activity-reports", { params });
      setRows(r.data.rows || []);
      setTotals(r.data.totals || {});
      setByArea(r.data.by_area_of_support || []);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || err.message); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const byType = useMemo(() => {
    const m = {};
    rows.forEach((r) => { m[r.contact_type] = (m[r.contact_type] || 0) + 1; });
    return Object.entries(m).sort((a, b) => b[1] - a[1]).map(([label, value]) => ({ label, value }));
  }, [rows]);

  const overTime = useMemo(() => {
    const span = daySpan(filters.date_from, filters.date_to);
    const useMonthly = span > 92;
    const m = {};
    rows.forEach((r) => {
      const key = useMonthly ? monthKey(r.activity_date) : r.activity_date;
      m[key] = (m[key] || 0) + 1;
    });
    return { data: Object.entries(m).sort().map(([k, v]) => ({ key: k, count: v })), useMonthly };
  }, [rows, filters.date_from, filters.date_to]);

  const exportCsv = () => {
    const headers = ["Activity Date","Time","First Name","Last Name","Company","Chaplain / Staff","Contact Type","Areas of Support","Status","Urgency","Follow-Up Needed","Follow-Up Date","Note"];
    const lines = [headers.join(",")];
    rows.forEach((r) => {
      lines.push([
        r.activity_date, r.activity_time, r.first_name, r.last_name, r.company_name,
        r.staff_name, r.contact_type,
        (r.areas_of_support || []).join("; "),
        r.status, r.urgency,
        r.follow_up_needed ? "Yes" : "No", r.follow_up_date || "", r.note || "",
      ].map(csvEscape).join(","));
    });
    const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url;
    a.download = `CCCHAPS_Activity_Report_${todayIso()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a); URL.revokeObjectURL(url);
  };

  const appliedFiltersText = Object.entries(filters).filter(([, v]) => v !== "" && v != null).map(([k, v]) => `${k}=${v}`).join(" · ") || "None";

  return (
    <div data-testid="admin-activity-reports">
      <style>{`
        @media print {
          .no-print { display: none !important; }
          .print-only { display: block !important; }
          body { background: white !important; }
          .print-avoid-break { page-break-inside: avoid; }
          .print-note { white-space: pre-wrap; }
        }
        .print-only { display: none; }
      `}</style>

      {/* Print header */}
      <div className="print-only mb-4">
        <p className="text-[10px] uppercase tracking-[0.24em] font-bold text-red-700">Confidential — CCCHAPS Internal Use Only</p>
        <h1 className="font-heading text-2xl mt-1">Chaplain Activity Report</h1>
        <p className="text-xs text-slate-600 mt-1">Generated {new Date().toLocaleString()} · Filters: {appliedFiltersText} · Total activities: {totals.total_activities}</p>
      </div>

      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap no-print">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900">Chaplain Activity Reports</h2>
          <p className="text-sm text-slate-500">Review, filter, print, and export confidential CCCHAPS service activity.</p>
        </div>
        <div className="flex gap-2">
          <button onClick={load} data-testid="ar-refresh" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:border-[#4A6FA5]"><RefreshCw className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} strokeWidth={2.2} /> Refresh</button>
          <button onClick={exportCsv} data-testid="ar-csv" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-[#4A6FA5] hover:bg-[#3B597B] text-white text-sm font-semibold uppercase tracking-wider"><Download className="w-4 h-4" strokeWidth={2.2} /> Export CSV</button>
          <button onClick={() => window.print()} data-testid="ar-print" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm hover:border-[#4A6FA5]"><Printer className="w-4 h-4" strokeWidth={2.2} /> Print / Save PDF</button>
        </div>
      </div>

      <div className="mb-4 flex items-start gap-2 text-xs text-[#3B597B] no-print"><ShieldCheck className="w-3.5 h-3.5 mt-0.5" strokeWidth={2.2} /><p>Internal admin report. Reads live production support-request activity only. Demo records are never included.</p></div>

      {/* Filters */}
      <div data-testid="ar-filters" className="mb-6 rounded-xl border border-slate-200 bg-white p-4 grid grid-cols-2 md:grid-cols-4 gap-3 no-print">
        <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">From</span><input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm" data-testid="ar-date-from" /></label>
        <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">To</span><input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm" data-testid="ar-date-to" /></label>
        <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Company</span><input value={filters.company} onChange={(e) => setFilters({ ...filters, company: e.target.value })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm" data-testid="ar-company" /></label>
        <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Chaplain / Staff</span><input value={filters.staff_name} onChange={(e) => setFilters({ ...filters, staff_name: e.target.value })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm" data-testid="ar-staff" /></label>
        <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Contact Type</span><select value={filters.contact_type} onChange={(e) => setFilters({ ...filters, contact_type: e.target.value })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm bg-white" data-testid="ar-contact-type"><option value="">Any</option>{CONTACT_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}</select></label>
        <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Status</span><select value={filters.status_filter} onChange={(e) => setFilters({ ...filters, status_filter: e.target.value })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm bg-white" data-testid="ar-status"><option value="">Any</option><option value="new">New</option><option value="in_progress">In Progress</option><option value="contacted">Contacted</option><option value="closed">Closed</option></select></label>
        <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Follow-Up Needed</span><select value={filters.follow_up_needed} onChange={(e) => setFilters({ ...filters, follow_up_needed: e.target.value })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm bg-white" data-testid="ar-fu"><option value="">Any</option><option value="true">Yes</option><option value="false">No</option></select></label>
        <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Follow-Up Date From</span><input type="date" value={filters.follow_up_date_from} onChange={(e) => setFilters({ ...filters, follow_up_date_from: e.target.value })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm" data-testid="ar-fu-date-from" /></label>
        <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Follow-Up Date To</span><input type="date" value={filters.follow_up_date_to} onChange={(e) => setFilters({ ...filters, follow_up_date_to: e.target.value })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm" data-testid="ar-fu-date-to" /></label>
        <label className="block text-xs"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Area of Support</span><select value={filters.area_of_support} onChange={(e) => setFilters({ ...filters, area_of_support: e.target.value })} className="mt-1 w-full px-2 py-1.5 rounded border border-slate-300 text-sm bg-white" data-testid="ar-area"><option value="">Any</option>{areasOfSupport.map((a) => <option key={a} value={a}>{a}</option>)}</select></label>
        <label className="block text-xs col-span-2 md:col-span-1"><span className="font-semibold text-slate-600 uppercase tracking-wider text-[10px]">Search name/reason</span><div className="relative"><Search className="w-3.5 h-3.5 absolute left-2 top-2.5 text-slate-400" /><input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="mt-1 w-full pl-7 pr-2 py-1.5 rounded border border-slate-300 text-sm" data-testid="ar-q" /></div></label>
        <div className="col-span-2 md:col-span-4 flex justify-end"><button onClick={load} data-testid="ar-apply" className="px-4 py-1.5 rounded-lg bg-slate-800 hover:bg-slate-900 text-white text-xs font-semibold uppercase tracking-wider">Apply Filters</button></div>
      </div>

      {/* Summary tiles */}
      <div data-testid="ar-tiles" className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6 print-avoid-break">
        <Tile label="Total Activities" value={totals.total_activities} testId="tile-total" />
        <Tile label="Unique Employees" value={totals.unique_employees} testId="tile-employees" />
        <Tile label="Unique Chaplains" value={totals.unique_chaplains} testId="tile-chaplains" />
        <Tile label="With Follow-Up" value={totals.with_follow_up} testId="tile-followup" />
      </div>

      {/* Charts */}
      <div className="grid lg:grid-cols-2 gap-4 mb-6 print-avoid-break">
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-3">Activities by contact type</p>
          <div className="h-[260px]" data-testid="ar-chart-type">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={byType} margin={{ top: 5, right: 8, left: 0, bottom: 40 }}>
                <CartesianGrid stroke="#EDF1F6" strokeDasharray="3 3" />
                <XAxis dataKey="label" tick={{ fill: "#64748b", fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                <Bar dataKey="value" fill="#4A6FA5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4">
          <p className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-3">Activity over time ({overTime.useMonthly ? "monthly" : "daily"})</p>
          <div className="h-[260px]" data-testid="ar-chart-time">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={overTime.data} margin={{ top: 5, right: 8, left: 0, bottom: 20 }}>
                <CartesianGrid stroke="#EDF1F6" strokeDasharray="3 3" />
                <XAxis dataKey="key" tick={{ fill: "#64748b", fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 11 }} />
                <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                <Legend wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="count" name="Activities" stroke="#4A6FA5" strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="rounded-xl border border-slate-200 bg-white p-4 lg:col-span-2">
          <p className="text-xs uppercase tracking-[0.16em] font-semibold text-slate-500 mb-3">Activities by area of support <span className="normal-case tracking-normal text-slate-400 text-[11px] font-normal">(multi-tag weighted — one activity contributes 1 per area)</span></p>
          <div className="h-[260px]" data-testid="ar-chart-area">
            {byArea.length === 0 ? (
              <div className="h-full flex items-center justify-center text-xs text-slate-400" data-testid="ar-chart-area-empty">No areas of support recorded for the current filter set.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={byArea} margin={{ top: 5, right: 8, left: 0, bottom: 40 }}>
                  <CartesianGrid stroke="#EDF1F6" strokeDasharray="3 3" />
                  <XAxis dataKey="area" tick={{ fill: "#64748b", fontSize: 10 }} interval={0} angle={-25} textAnchor="end" height={70} />
                  <YAxis allowDecimals={false} tick={{ fill: "#64748b", fontSize: 11 }} />
                  <Tooltip contentStyle={{ borderRadius: 6, fontSize: 12 }} />
                  <Bar dataKey="count" fill="#7A5FB0" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="ar-empty">No activities match the current filters.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-[11px] uppercase tracking-[0.14em] text-slate-500">
                <tr>
                  <th className="text-left px-3 py-3 w-8"></th>
                  <th className="text-left px-3 py-3">Date</th>
                  <th className="text-left px-3 py-3">Time</th>
                  <th className="text-left px-3 py-3">Employee / Request</th>
                  <th className="text-left px-3 py-3">Organization</th>
                  <th className="text-left px-3 py-3">Chaplain</th>
                  <th className="text-left px-3 py-3">Contact Type</th>
                  <th className="text-left px-3 py-3">Areas of Support</th>
                  <th className="text-left px-3 py-3">Status</th>
                  <th className="text-left px-3 py-3">Follow-Up</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {rows.map((r) => (
                  <FragmentRow key={r.activity_id} r={r} expanded={expanded} setExpanded={setExpanded} />
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <p className="mt-4 text-[10px] text-slate-400 no-print">Areas of Support are structured tags recorded per activity. Aggregate employer utilization reporting will use these tags only — free-text notes and reasons are never analyzed.</p>
    </div>
  );
}

function Tile({ label, value, testId }) {
  return (
    <div data-testid={testId} className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500">{label}</p>
      <div className="mt-1 font-heading text-3xl text-slate-900 tracking-tight">{value ?? 0}</div>
    </div>
  );
}

function FragmentRow({ r, expanded, setExpanded }) {
  const isOpen = expanded === r.activity_id;
  return (
    <>
      <tr className="hover:bg-slate-50/60" data-testid={`ar-row-${r.activity_id}`}>
        <td className="px-3 py-3"><button onClick={() => setExpanded(isOpen ? null : r.activity_id)} data-testid={`ar-expand-${r.activity_id}`}>{isOpen ? <ChevronUp className="w-4 h-4" strokeWidth={2.2} /> : <ChevronDown className="w-4 h-4" strokeWidth={2.2} />}</button></td>
        <td className="px-3 py-3 text-slate-800">{r.activity_date}</td>
        <td className="px-3 py-3 text-slate-600">{r.activity_time}</td>
        <td className="px-3 py-3 text-slate-900">{r.first_name} {r.last_name}</td>
        <td className="px-3 py-3 text-slate-600">{r.company_name || "—"}</td>
        <td className="px-3 py-3 text-slate-600">{r.staff_name}</td>
        <td className="px-3 py-3"><span className="inline-flex px-2 py-0.5 rounded-full bg-[#F0F4F8] text-[#3B597B] text-[11px] font-semibold uppercase tracking-wider">{r.contact_type}</span></td>
        <td className="px-3 py-3">
          {Array.isArray(r.areas_of_support) && r.areas_of_support.length > 0 ? (
            <div className="flex flex-wrap gap-1" data-testid={`ar-areas-${r.activity_id}`}>
              {r.areas_of_support.map((a) => (
                <span key={a} className="inline-flex px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 text-[11px] font-medium">{a}</span>
              ))}
            </div>
          ) : (
            <span className="text-slate-400 text-xs">—</span>
          )}
        </td>
        <td className="px-3 py-3 text-slate-600">{r.status}</td>
        <td className="px-3 py-3 text-xs">{r.follow_up_needed ? (<span className="text-amber-800">Yes{r.follow_up_date ? ` · ${r.follow_up_date}` : ""}</span>) : "—"}</td>
      </tr>
      {isOpen && (
        <tr className="bg-slate-50/60" data-testid={`ar-detail-${r.activity_id}`}><td colSpan={10} className="px-3 py-4">
          <div className="grid md:grid-cols-2 gap-4 text-sm">
            <div><p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-1">Activity Note</p><p className="text-slate-700 leading-relaxed print-note whitespace-pre-wrap">{r.note || "—"}</p></div>
            <div><p className="text-[10px] uppercase tracking-[0.16em] font-semibold text-slate-500 mb-1">Support-Request Reason</p><p className="text-slate-700 leading-relaxed print-note whitespace-pre-wrap">{r.reason || "—"}</p></div>
          </div>
          <div className="mt-3 flex flex-wrap gap-4 text-[11px] text-slate-500"><span>Urgency: {r.urgency}</span>{r.activity_updated_at && <span>Edited: {new Date(r.activity_updated_at).toLocaleString()} by {r.activity_updated_by_name || "—"}</span>}</div>
        </td></tr>
      )}
    </>
  );
}
